import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { debounce } from 'lodash';
import { fetchCompanyList } from 'redux/actions/companyAction';
import {
  addSettlement,
  getConsolidatedInvoiceList,
} from 'redux/actions/SettlementAction';
import { getServiceCity } from 'redux/actions/UserServiceCity';
import useModal from 'hooks/useModal';
import { formatCurrency, getDateFormat } from 'utils/utils';
import CommonDateRangePicker from 'components/commonDateRangePicker/CommonDateRangePicker';
import Loader from 'components/loader/Loader';
import BillingFilter from 'pages/operation/billing/billingFlow/billingFilter/BillingFilter';
import {
  ChequeDetails,
  DemandDraftDetails,
  rtgsDetails,
  paymentRadioBtn,
  paymentFilterOptions,
} from './PaymentData';
import PaymentHistory from './PaymentHistory';
import PaymentTable from './PaymentTable';
import selectCompany from 'assets/images/paymentSelect.svg';

const NewPayment = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { open, toggle } = useModal();
  const [chequeData, setChequeData] = useState('');
  const [radioSelected, setRadioSelected] = useState('');
  const [showInvoice, setShowInvoice] = useState(false);
  const [updateBill, setUpdateBill] = useState(false);
  const [submitFormData, setSubmitFormData] = useState();
  const [paymentTitle, setPaymentTitle] = useState();
  const [billingFilter, setBillingFilter] = useState({
    companyName: {},
    city: {},
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dateFrom, setDateFrom] = useState(getDateFormat(new Date(), "reservationDateFormat"));
  const [dateTo, setDateTo] = useState(getDateFormat(new Date(), "reservationDateFormat"));
  const [selectedRow, setSelectedRow] = useState(0);
  const [billAmount, setBillAmount] = useState('');
  const [billInvoice, setBillInvoice] = useState('');
  const [showSelectCompany, setShowSelectCompany] = useState(false);

  let filterData = {
    search: '',
  };

  let payloadData = {
    city: billingFilter?.city?.value,
    companyId: billingFilter?.companyName?.value,
    dateFrom: dateFrom ? dateFrom : '',
    dateTo: dateTo ? dateTo : '',
    page: page,
    pageSize: rowsPerPage,
  };

  useEffect(() => {
    dispatch(
      fetchCompanyList(filterData, {
        searchType: 'globalSearch',
        loader: 'backDropLoader',
      })
    );
    dispatch(getServiceCity());
  }, []);

  const companyTableData = useSelector(
    (state) => state?.userCompany?.companyList?.body
  );
  const locationOptions = useSelector(
    (state) => state?.userServiceCity?.userServiceCity?.data
  );

  const companyOption =
    companyTableData?.length > 0 &&
    companyTableData?.map((val) => {
      return { value: val?.id, label: val.name };
    });

  const cityOption =
    locationOptions?.length > 0 &&
    locationOptions?.map((val) => {
      return { value: val?.id, label: val.name };
    });

  const companyInvoiceList = useSelector(
    (state) => state?.getConsolidateInvoiveList?.consolidatedInvoiceData?.data
  );

  const companyInvoiceLoading = useSelector(
    (state) => state?.getConsolidateInvoiveList?.loading
  );

  useEffect(() => {
    if (cityOption.length === 1) {
      setBillingFilter((prevState) => ({
        ...prevState,
        city: cityOption[0],
      }));
    }
  }, [cityOption.length === 1]);

  // ---FormData for Payment---
  const getFormDataForPaymentMode = (paymentMode) => {
    // Set formData based on the selected radio button
    switch (paymentMode) {
      case 'Cheque':
        return ChequeDetails;
      case 'DemandDraft':
        return DemandDraftDetails;
      case 'RTGS':
        return rtgsDetails;
      default:
        return null;
    }
  };

  const getTitleForPaymentMode = (paymentMode) => {
    switch (paymentMode) {
      case 'Cheque':
        return 'Cheque Details';
      case 'DemandDraft':
        return 'Demand Draft Details';
      case 'RTGS':
        return 'RTGS Details';
      default:
        return '';
    }
  };

  const defaultValues = {
    payee: billingFilter?.companyName?.label,
  };

  const handleFilter = (name, newValue) => {
    setBillingFilter((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));

    if (name === 'companyName') {
      payloadData['companyId'] = newValue?.value;
      if (newValue === null) {
        payloadData['city'] = '';
        setBillingFilter({ city: null });
      }
    } else if (name === 'city') {
      payloadData['city'] = newValue?.value;
      if (newValue === null) {
        payloadData['companyId'] = '';
        setBillingFilter({ companyName: null });
      }
    }
    // Need to call company payment API
    if (payloadData['companyId'] && payloadData['city']) {
      // Call the company payment API
      dispatch(getConsolidatedInvoiceList(payloadData));
      setShowSelectCompany(true);
    } else {
      setShowSelectCompany(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    dispatch(getConsolidatedInvoiceList({ ...payloadData, page: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
    dispatch(
      getConsolidatedInvoiceList({
        ...payloadData,
        page: 0,
        pageSize: parseInt(event.target.value),
      })
    );
  };

  const handleInput = (e, value) => {
    setRadioSelected(value);

    // Reset formData when radio selection changes
    const newFormData = getFormDataForPaymentMode(value);
    setSubmitFormData(newFormData);
    setPaymentTitle(getTitleForPaymentMode(value));
    setShowInvoice(true);
  };

  const onDialog = (val, image) => {
    const paymentDetailsMap = {
      Cheque: {
        consolidatedInvoiceId: billInvoice?.id,
        companyId: billInvoice?.receipientCompanyId,
        modeOfPayment: 'Cheque',
        payeeName: val?.payee,
        settledAt: getDateFormat(val?.chooseDate['$d'], 'timeFormat'),
        bankName: val?.bankName,
        amount: val?.amount,
        chequeNo: val?.chequeNo,
        tds: val?.tds,
        remark: val?.remark,
      },
      DemandDraft: {
        consolidatedInvoiceId: billInvoice?.id,
        companyId: billInvoice?.receipientCompanyId,
        modeOfPayment: 'DemandDraft',
        payeeName: val?.payee,
        settledAt: getDateFormat(val?.chooseDate['$d'], 'timeFormat'),
        ddNo: val?.demandNo,
        bankName: val?.bankName,
        amount: val?.amount,
        tds: val?.tds,
        remark: val?.remark,
      },
      RTGS: {
        consolidatedInvoiceId: billInvoice?.id,
        companyId: billInvoice?.receipientCompanyId,
        modeOfPayment: 'RTGS/NEFT',
        payeeName: val?.payee,
        settledAt: getDateFormat(val?.chooseDate['$d'], 'timeFormat'),
        bankName: val?.bankName,
        branchName: val?.branchName,
        amount: val?.amount,
        accountNo: val?.accountNo,
        utrTransactionId: val?.utrTransactionId,
        timeOfReceipt: val?.receiptTime,
        branchName: val?.branchName,
        tds: val?.tds,
        remark: val?.remark,
      },
    };

    const selectedPaymentDetails = paymentDetailsMap[radioSelected];

    let formData = new FormData();
    formData.append('settlementImg', image);

    Object.keys(selectedPaymentDetails).map((val) => {
      formData.append(val, selectedPaymentDetails[val]);
    });
    dispatch(addSettlement(formData, navigate));
  };

  const handleIcon = () => {
    toggle();
    setUpdateBill(true);
  };

  const onSelectDateRange = (newValue) => {
    if (newValue === null || newValue === 0) {
      setDateFrom('');
      setDateTo('');
      filterData = { ...payloadData, dateFrom: '', dateTo: '' };
    } else {
      setDateFrom(getDateFormat(newValue[0], 'reservationDateFormat'));
      setDateTo(getDateFormat(newValue[1], 'reservationDateFormat'));
      filterData = {
        ...payloadData,
        dateFrom: getDateFormat(newValue[0], 'reservationDateFormat'),
        dateTo: getDateFormat(newValue[1], 'reservationDateFormat'),
      };
    }
    if(billingFilter?.city?.value && billingFilter?.companyName?.value){
    dispatch(getConsolidatedInvoiceList(filterData));}
  };

  useEffect(() => {
    // Check if companyInvoiceList has data and it's not an empty array
    if (companyInvoiceList?.body?.length > 0) {
      // Set billAmount based on the total amount of the first selected row
      setBillAmount(companyInvoiceList?.body?.[0]?.outstandingAmount);
      setBillInvoice(companyInvoiceList?.body?.[0]);
    }
  }, [companyInvoiceList]);

  // Handle Radio Selected
  const handleRadioSelected = (index, isExpandIconClick, body) => {
    setSelectedRow(index); // Set selected row when clicked
    setBillAmount(body?.outstandingAmount);
    setBillInvoice(body);
  };

  //company Auto suggestion
  const onHandleAutoSuggestion = debounce((event, value, fieldName) => {
    if (event?.type === 'change') {
      if (fieldName === 'companyName' && value) {
        dispatch(
          fetchCompanyList(
            { ...filterData, search: value },
            { searchType: 'globalSearch', loader: 'backDropLoader' }
          )
        );
      } else {
        dispatch(
          fetchCompanyList(filterData, {
            searchType: 'globalSearch',
            loader: 'backDropLoader',
          })
        );
      }
    }
  }, 500);

  return (
    <Box>
      <Grid container className="justifyBetween">
        <Typography className="colorPrimary fw600" variant="head3">
          New Payment
        </Typography>
        <CommonDateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onSelectDateRange={onSelectDateRange}
        />
      </Grid>
      <Grid
        container
        gap={3}
        flexWrap={'nowrap'}
        direction="row"
        justifyContent="flex-start"
        alignitem="center"
        className="pt30"
      >
        {paymentFilterOptions(companyOption, cityOption) &&
          paymentFilterOptions(companyOption, cityOption)?.map(
            (formData, index) => (
              <Grid
                item
                md={4}
                sm={6}
                xs={12}
                key={index}
                className="commonMultiform"
              >
                <BillingFilter
                  billingFilterValue={billingFilter}
                  setBillingFilter={setBillingFilter}
                  handleFilter={handleFilter}
                  formData={formData}
                  onHandleAutoSuggestion={onHandleAutoSuggestion}
                />
              </Grid>
            )
          )}
      </Grid>
      {companyInvoiceLoading ? (
        <Loader />
      ) : (
        <>
          {showSelectCompany === false ? (
            <div className=" mt40 mtb30">
              <div className="justifyCenter mb25">
                <img src={selectCompany} alt="company" />
              </div>
              <Typography variant="head5" className="fw600 justifyCenter">
                Please select company and location to get the respective
                settlements.
              </Typography>
            </div>
          ) : (
            <>
              <Grid item xs={12} md={12} className="ptb30">
                <PaymentTable
                  invoiceTableData={companyInvoiceList}
                  handleRadioSelected={handleRadioSelected}
                  selectedRow={selectedRow}
                  companyInvoiceLoading={companyInvoiceLoading}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                />
              </Grid>

              {companyInvoiceList?.body?.length > 0 && (
                <Grid
                  container
                  className="mtb20"
                  justifyContent={'space-between'}
                >
                  <Grid item xs={7} className="height122">
                    <Box className="commonBorder p20 br10">
                      <Typography className={'colorPrimary'} variant="body1">
                        Payments Mode
                      </Typography>
                      <FormControl>
                        <RadioGroup
                          row
                          aria-labelledby="demo-row-radio-buttons-group-label"
                          onChange={(e) => {
                            handleInput(e, e.target.value);
                          }}
                        >
                          {paymentRadioBtn?.map((value) => (
                            <FormControlLabel
                              key={value.label}
                              value={value.value || ''}
                              control={<Radio />}
                              label={value.label}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  </Grid>
                  <Grid item xs={4} className="height122">
                    <Box className="commonBorder p20 br10 bgLightPrimary">
                      <Typography variant="body2" className="justifyCenter">
                        Bill
                      </Typography>
                      <Tooltip title={'₹ ' + (formatCurrency(billAmount) || 0)}>
                        <Typography
                          variant="head3"
                          className="colorPrimary fw600 justifyCenter"
                        >
                          {'₹ ' + formatCurrency(billAmount) || 0}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </Grid>
                </Grid>
              )}

              {showInvoice && (
                <PaymentHistory
                  formData={submitFormData}
                  paymentName={paymentTitle}
                  handleIcon={handleIcon}
                  button={'submit'}
                  onSubmittBtn={onDialog}
                  radioSelected={radioSelected}
                  onHandleAutoSuggestion={onHandleAutoSuggestion}
                  defaultValues={defaultValues}
                />
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default NewPayment;
